import { useState, useEffect } from 'react';
import { useLocalNavigate as useNavigate, useLocale, localizePath } from '@/lib/locale';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@/lib/catalog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, CreditCard, Package, ChevronRight, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = ({ cart, onClearCart }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = useLocale();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [shippingData, setShippingData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    streetAddress: '',
    apartment: '',
    postalCode: ''
  });

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  useEffect(() => {
    if (step === 1) {
      // Auto-focus first input
      const firstInput = document.getElementById('firstName');
      if (firstInput) firstInput.focus();
    }
  }, [step]);

  const validateShippingForm = () => {
    const newErrors = {};
    
    const required = (key, label) => {
      if (!shippingData[key].trim()) newErrors[key] = t('checkout.required', { field: t(`checkout.${label}`) });
    };
    required('firstName', 'firstName');
    required('lastName', 'lastName');
    required('email', 'email');
    if (!newErrors.email && !/\S+@\S+\.\S+/.test(shippingData.email)) newErrors.email = t('checkout.emailInvalid');
    required('phone', 'phone');
    required('country', 'country');
    required('city', 'city');
    required('streetAddress', 'street');
    required('postalCode', 'postalCode');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleShippingSubmit = (e) => {
    e.preventDefault();
    if (validateShippingForm()) {
      setStep(2);
    } else {
      toast.error(t('checkout.fillRequired'));
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error(t('checkout.cartEmpty'));
      return;
    }

    setLoading(true);
    try {
      // Stripe returns to {origin_url}/checkout/success, so keep the language prefix
      const origin_url = window.location.origin + localizePath('/', lang).replace(/\/$/, '');
      
      const items = cart.map(item => ({
        product_id: item.product_id,
        product_name: item.selected_variant ? `${item.name} — ${item.selected_variant}` : item.name,
        quantity: item.quantity,
        price: item.price,
        selected_variant: item.selected_variant || null
      }));

      const response = await axios.post(`${API}/checkout/create-session`, {
        customer_email: shippingData.email,
        customer_name: `${shippingData.firstName} ${shippingData.lastName}`,
        shipping_address: {
          firstName: shippingData.firstName,
          lastName: shippingData.lastName,
          phone: shippingData.phone,
          country: shippingData.country,
          city: shippingData.city,
          streetAddress: shippingData.streetAddress,
          apartment: shippingData.apartment,
          postalCode: shippingData.postalCode
        },
        items,
        origin_url
      });

      // Store shipping data in localStorage for success page
      localStorage.setItem('shipping_data', JSON.stringify(shippingData));

      // Redirect to Stripe
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(t('checkout.sessionFailed'));
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-10 pb-20 flex items-center justify-center" data-testid="checkout-page">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">{t('checkout.cartEmpty')}</p>
          <Button onClick={() => navigate('/shop')}>{t('checkout.continueShopping')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-10 pb-20 bg-background" data-testid="checkout-page">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= 1 ? 'bg-accent text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  1
                </div>
                <span className={`ml-2 text-sm font-medium ${step >= 1 ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {t('checkout.stepShipping')}
                </span>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= 2 ? 'bg-accent text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  2
                </div>
                <span className={`ml-2 text-sm font-medium ${step >= 2 ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {t('checkout.stepPayment')}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                      {t('checkout.shippingTitle')}
                    </h1>
                    <p className="text-muted-foreground">{t('checkout.shippingSubtitle')}</p>
                  </div>

                  <form onSubmit={handleShippingSubmit} className="space-y-6">
                    <div className="p-8 rounded-2xl bg-card border border-border space-y-6">
                      {/* Name Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="firstName" className="text-foreground">
                            {t('checkout.firstName')} <span className="text-accent">*</span>
                          </Label>
                          <Input
                            id="firstName"
                            data-testid="firstName-input"
                            type="text"
                            value={shippingData.firstName}
                            onChange={(e) => setShippingData({ ...shippingData, firstName: e.target.value })}
                            className={`mt-2 bg-input border-border text-foreground ${errors.firstName ? 'border-red-500' : ''}`}
                          />
                          {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                        </div>

                        <div>
                          <Label htmlFor="lastName" className="text-foreground">
                            {t('checkout.lastName')} <span className="text-accent">*</span>
                          </Label>
                          <Input
                            id="lastName"
                            data-testid="lastName-input"
                            type="text"
                            value={shippingData.lastName}
                            onChange={(e) => setShippingData({ ...shippingData, lastName: e.target.value })}
                            className={`mt-2 bg-input border-border text-foreground ${errors.lastName ? 'border-red-500' : ''}`}
                          />
                          {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                        </div>
                      </div>

                      {/* Contact Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="email" className="text-foreground">
                            {t('checkout.email')} <span className="text-accent">*</span>
                          </Label>
                          <Input
                            id="email"
                            data-testid="email-input"
                            type="email"
                            value={shippingData.email}
                            onChange={(e) => setShippingData({ ...shippingData, email: e.target.value })}
                            className={`mt-2 bg-input border-border text-foreground ${errors.email ? 'border-red-500' : ''}`}
                          />
                          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                        </div>

                        <div>
                          <Label htmlFor="phone" className="text-foreground">
                            {t('checkout.phone')} <span className="text-accent">*</span>
                          </Label>
                          <Input
                            id="phone"
                            data-testid="phone-input"
                            type="tel"
                            value={shippingData.phone}
                            onChange={(e) => setShippingData({ ...shippingData, phone: e.target.value })}
                            className={`mt-2 bg-input border-border text-foreground ${errors.phone ? 'border-red-500' : ''}`}
                          />
                          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                        </div>
                      </div>

                      {/* Address Fields */}
                      <div>
                        <Label htmlFor="streetAddress" className="text-foreground">
                          {t('checkout.street')} <span className="text-accent">*</span>
                        </Label>
                        <Input
                          id="streetAddress"
                          data-testid="streetAddress-input"
                          type="text"
                          value={shippingData.streetAddress}
                          onChange={(e) => setShippingData({ ...shippingData, streetAddress: e.target.value })}
                          className={`mt-2 bg-input border-border text-foreground ${errors.streetAddress ? 'border-red-500' : ''}`}
                        />
                        {errors.streetAddress && <p className="text-red-500 text-sm mt-1">{errors.streetAddress}</p>}
                      </div>

                      <div>
                        <Label htmlFor="apartment" className="text-foreground">
                          {t('checkout.apartment')} <span className="text-muted-foreground text-sm">{t('checkout.optional')}</span>
                        </Label>
                        <Input
                          id="apartment"
                          data-testid="apartment-input"
                          type="text"
                          value={shippingData.apartment}
                          onChange={(e) => setShippingData({ ...shippingData, apartment: e.target.value })}
                          className="mt-2 bg-input border-border text-foreground"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <Label htmlFor="city" className="text-foreground">
                            {t('checkout.city')} <span className="text-accent">*</span>
                          </Label>
                          <Input
                            id="city"
                            data-testid="city-input"
                            type="text"
                            value={shippingData.city}
                            onChange={(e) => setShippingData({ ...shippingData, city: e.target.value })}
                            className={`mt-2 bg-input border-border text-foreground ${errors.city ? 'border-red-500' : ''}`}
                          />
                          {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                        </div>

                        <div>
                          <Label htmlFor="postalCode" className="text-foreground">
                            {t('checkout.postalCode')} <span className="text-accent">*</span>
                          </Label>
                          <Input
                            id="postalCode"
                            data-testid="postalCode-input"
                            type="text"
                            value={shippingData.postalCode}
                            onChange={(e) => setShippingData({ ...shippingData, postalCode: e.target.value })}
                            className={`mt-2 bg-input border-border text-foreground ${errors.postalCode ? 'border-red-500' : ''}`}
                          />
                          {errors.postalCode && <p className="text-red-500 text-sm mt-1">{errors.postalCode}</p>}
                        </div>

                        <div>
                          <Label htmlFor="country" className="text-foreground">
                            {t('checkout.country')} <span className="text-accent">*</span>
                          </Label>
                          <Input
                            id="country"
                            data-testid="country-input"
                            type="text"
                            value={shippingData.country}
                            onChange={(e) => setShippingData({ ...shippingData, country: e.target.value })}
                            className={`mt-2 bg-input border-border text-foreground ${errors.country ? 'border-red-500' : ''}`}
                          />
                          {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
                        </div>
                      </div>

                      {/* Discreet Shipping Notice */}
                      <div className="p-4 rounded-xl bg-accent/10 border border-accent/30">
                        <div className="flex items-start gap-3">
                          <Package className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <p className="text-foreground font-semibold mb-1">{t('checkout.discreetTitle')}</p>
                            <p className="text-muted-foreground">{t('checkout.discreetText')}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button
                      data-testid="continue-to-payment-btn"
                      type="submit"
                      className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold py-6 text-lg rounded-xl"
                    >
                      {t('checkout.continueToPayment')}
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </form>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                      {t('checkout.reviewTitle')}
                    </h1>
                    <p className="text-muted-foreground">{t('checkout.reviewSubtitle')}</p>
                  </div>

                  {/* Shipping Info Review */}
                  <div className="p-8 rounded-2xl bg-card border border-border">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-foreground">{t('checkout.shippingAddress')}</h3>
                      <button
                        onClick={() => setStep(1)}
                        className="text-sm text-accent hover:text-accent/90 flex items-center gap-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        {t('checkout.edit')}
                      </button>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p className="text-foreground font-medium">{shippingData.firstName} {shippingData.lastName}</p>
                      <p>{shippingData.streetAddress}</p>
                      {shippingData.apartment && <p>{shippingData.apartment}</p>}
                      <p>{shippingData.city}, {shippingData.postalCode}</p>
                      <p>{shippingData.country}</p>
                      <p className="pt-2">{shippingData.email}</p>
                      <p>{shippingData.phone}</p>
                    </div>
                  </div>

                  <form onSubmit={handlePaymentSubmit}>
                    <div className="p-8 rounded-2xl bg-card border border-border space-y-4">
                      <div className="flex items-center gap-2 mb-4">
                        <CreditCard className="w-5 h-5 text-accent" />
                        <h3 className="text-lg font-semibold text-foreground">{t('checkout.paymentMethod')}</h3>
                      </div>

                      <div className="p-4 rounded-lg bg-muted/30 border border-border">
                        <p className="text-sm text-muted-foreground">
                          {t('checkout.stripeRedirect')}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
                        <ShieldCheck className="w-5 h-5 text-accent" />
                        <span>{t('checkout.securedByStripe')}</span>
                      </div>
                    </div>

                    <Button
                      data-testid="proceed-to-stripe-btn"
                      type="submit"
                      disabled={loading}
                      className="w-full mt-6 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold py-6 text-lg rounded-xl"
                    >
                      {loading ? t('checkout.processing') : t('checkout.pay', { price: formatPrice(total) })}
                    </Button>
                  </form>
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="p-8 rounded-2xl bg-card border border-border space-y-6 sticky top-24">
                <h2 className="text-xl font-heading font-semibold text-foreground">{t('checkout.orderSummary')}</h2>
                
                <div className="space-y-4">
                  {cart.map((item) => {
                    const key = `${item.product_id}::${item.selected_variant || ''}`;
                    return (
                    <div key={key} className="flex gap-4" data-testid={`summary-item-${key}`}>
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-foreground text-sm">{item.name}</p>
                        {item.selected_variant && (
                          <p className="text-xs text-muted-foreground">{t('cart.size')}: {item.selected_variant}</p>
                        )}
                        <p className="text-sm text-muted-foreground">{t('checkout.qty', { count: item.quantity })}</p>
                      </div>
                      <p className="font-semibold text-accent text-sm">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  );})}
                </div>

                <div className="pt-4 border-t border-border space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('checkout.subtotal')}</span>
                    <span className="text-foreground">{formatPrice(total)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('checkout.shipping')}</span>
                    <span className="text-accent font-semibold">{t('checkout.free')}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg pt-3 border-t border-border">
                    <span className="font-semibold text-foreground">{t('checkout.total')}</span>
                    <span className="font-bold text-accent text-2xl" data-testid="summary-total">{formatPrice(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
